/**
 * CamerasPage — "Body Cameras" device listing (May 2026 Figma).
 *
 * Toolbar:
 *   [🔍]  Body Cameras  (?)
 *         N TOTAL · N ACTIVE · N OFFLINE · N ERRORS · N PANIC          [⋯][▤]
 *
 * Table columns:
 *   [ ] BODY CAMERA NAME ↑   SERIAL NUMBER   IP ADDRESS   SITE
 *       PROFILE   FIRMWARE   STATUS   [pen]
 *
 * Status pill colours:
 *   Online      → green
 *   Offline     → gray
 *   Error       → red
 *   Panic state → amber
 *
 * Backed by deviceService.listDevices() (GET /api/orgs/{orgId}/devices).
 */
import { useEffect, useMemo, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import deviceService from '../services/deviceService';
import CameraDrawer from '../components/cameras/CameraDrawer';

const PAGE_SIZE = 50;

/** Map a device row to a display status + pill class. */
function deriveStatus(d) {
  const s = String(d.currentStatus || '').toUpperCase();
  if (s === 'PANIC' || s === 'PANIC_STATE')
    return { key: 'panic',   label: 'Panic state', cls: 'cam-status cam-status-panic',   icon: 'fa-exclamation' };
  if (s === 'ERROR' || s === 'FAILED')
    return { key: 'error',   label: 'Error',       cls: 'cam-status cam-status-error',   icon: 'fa-times' };
  if (s === 'OFFLINE' || s === 'INACTIVE')
    return { key: 'offline', label: 'Offline',     cls: 'cam-status cam-status-offline', icon: 'fa-circle-notch' };
  if (s === 'UPDATING' || s === 'FOTA_IN_PROGRESS')
    return { key: 'updating', label: 'Updating…',  cls: 'cam-status cam-status-updating', icon: 'fa-circle-notch fa-spin' };
  return   { key: 'active',  label: 'Online',      cls: 'cam-status cam-status-active',  icon: 'fa-circle' };
}

export default function CamerasPage() {
  const { orgId, siteId } = useTenant();

  const [rows, setRows]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState('');
  const [showSearch, setShowSearch]   = useState(false);
  const [selected, setSelected]       = useState(() => new Set());
  const [sort, setSort]               = useState({ key: 'deviceName', asc: true });
  const [editingDevice, setEditingDevice] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const refreshList = () => setRefreshTick((t) => t + 1);

  useEffect(() => {
    if (!orgId) { setRows([]); setTotal(0); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    deviceService.listDevices(orgId, undefined, {
      siteId: siteId || undefined,
      limit: PAGE_SIZE,
      offset: 0,
      sortBy: 'deviceId',
      sortOrder: 'asc',
    })
      .then((res) => {
        if (cancelled) return;
        setRows(res?.content ?? res?.items ?? []);
        setTotal(res?.totalElements ?? (res?.content?.length ?? 0));
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message ?? e.message ?? 'Failed to load body cameras');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orgId, siteId, refreshTick]);

  const summary = useMemo(() => {
    let active = 0, offline = 0, errors = 0, panic = 0;
    rows.forEach((d) => {
      const s = deriveStatus(d);
      if (s.key === 'active')        active++;
      else if (s.key === 'offline')  offline++;
      else if (s.key === 'error')    errors++;
      else if (s.key === 'panic')    panic++;
    });
    return { active, offline, errors, panic };
  }, [rows]);

  const filteredSorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const base = !q ? rows : rows.filter((d) => {
      const hay = [
        d.deviceName, d.deviceId, d.serialNumber, d.ipAddress,
        d.siteName, d.profileName, d.templateName,
        d.firmwareVersion, d.currentStatus,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
    const get = (d) => {
      switch (sort.key) {
        case 'deviceName':   return d.deviceName || '';
        case 'serialNumber': return d.serialNumber || '';
        case 'ipAddress':    return d.ipAddress || '';
        case 'siteName':     return d.siteName || '';
        case 'profile':      return d.profileName || d.templateName || '';
        case 'firmware':     return d.firmwareVersion || '';
        case 'status':       return deriveStatus(d).label;
        default:             return '';
      }
    };
    return [...base].sort((a, b) =>
      (sort.asc ? 1 : -1) * String(get(a)).localeCompare(String(get(b))));
  }, [rows, search, sort]);

  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, asc: !p.asc } : { key, asc: true }));

  const allChecked  = filteredSorted.length > 0 && filteredSorted.every((d) => selected.has(d.deviceId));
  const someChecked = !allChecked && filteredSorted.some((d) => selected.has(d.deviceId));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allChecked) filteredSorted.forEach((d) => next.delete(d.deviceId));
    else            filteredSorted.forEach((d) => next.add(d.deviceId));
    return next;
  });
  const toggleRow = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleEdit = (d) => setEditingDevice(d);

  const ThSort = ({ id, label }) => (
    <th>
      <button className="data-table-sort" onClick={() => toggleSort(id)}>
        {label}
        <i className={`fas fa-sort${sort.key === id ? (sort.asc ? '-up' : '-down') : ''}`}></i>
      </button>
    </th>
  );

  return (
    <>
      <div className="sp-toolbar">
        <div className="sp-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search body cameras"
            onClick={() => setShowSearch((v) => !v)}
          >
            <i className="fas fa-search"></i>
          </button>
          <div className="sp-heading">
            <h1>
              Body Cameras
              <i className="far fa-question-circle bc-help" title="Body cameras assigned to this organization" />
            </h1>
            <div className="up-summary">
              <span className="up-summary-item"><strong>{total || rows.length}</strong> TOTAL</span>
              <span className="up-summary-item">
                <span className="up-dot up-dot-active" /> {summary.active} ACTIVE
              </span>
              <span className="up-summary-item">
                <span className="up-dot up-dot-inactive" /> {summary.offline} OFFLINE
              </span>
              <span className="up-summary-item bc-err">
                <i className="fas fa-times-circle" /> {summary.errors} ERRORS
              </span>
              <span className="up-summary-item bc-panic">
                <i className="fas fa-exclamation-circle" /> {summary.panic} PANIC
              </span>
            </div>
            {showSearch && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search body cameras..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="sp-toolbar-right">
          <button type="button" className="cm-icon-btn cm-icon-btn-circle" title="More" aria-label="More">
            <i className="fas fa-ellipsis-h"></i>
          </button>
          <button type="button" className="cm-icon-btn sp-view-toggle" title="Toggle view" aria-label="Toggle view">
            <i className="fas fa-table-list"></i>
          </button>
        </div>
      </div>

      {error && <div className="users-error">{error}</div>}

      <table className="data-table cm-table sp-table">
        <thead>
          <tr>
            <th className="cm-col-check">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => { if (el) el.indeterminate = someChecked; }}
                onChange={toggleAll}
                aria-label="Select all body cameras"
              />
            </th>
            <ThSort id="deviceName"   label="BODY CAMERA NAME" />
            <ThSort id="serialNumber" label="SERIAL NUMBER" />
            <ThSort id="ipAddress"    label="IP ADDRESS" />
            <ThSort id="siteName"     label="SITE" />
            <ThSort id="profile"      label="PROFILE" />
            <ThSort id="firmware"     label="FIRMWARE" />
            <ThSort id="status"       label="STATUS" />
            <th className="cm-col-actions" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr><td colSpan={9} style={{ textAlign: 'center', padding: 30 }}>
              <i className="fas fa-spinner fa-spin" /> Loading…
            </td></tr>
          )}
          {!loading && filteredSorted.length === 0 && (
            <tr><td colSpan={9} style={{ textAlign: 'center', padding: 30, color: 'var(--color-text-muted)' }}>
              No body cameras found.
            </td></tr>
          )}
          {!loading && filteredSorted.map((d) => {
            const st = deriveStatus(d);
            return (
              <tr key={d.deviceId}>
                <td className="cm-col-check">
                  <input
                    type="checkbox"
                    checked={selected.has(d.deviceId)}
                    onChange={() => toggleRow(d.deviceId)}
                    aria-label={`Select ${d.deviceName || d.deviceId}`}
                  />
                </td>
                <td className="up-cell-name">{d.deviceName || '—'}</td>
                <td>{d.serialNumber || <span className="up-muted">-</span>}</td>
                <td>{d.ipAddress    || <span className="up-muted">-</span>}</td>
                <td>{d.siteName     || <span className="up-muted">-</span>}</td>
                <td>{d.profileName  || d.templateName || <span className="up-muted">-</span>}</td>
                <td>{d.firmwareVersion || <span className="up-muted">-</span>}</td>
                <td>
                  <span className={st.cls}>
                    <i className={`fas ${st.icon}`}></i> {st.label}
                  </span>
                </td>
                <td className="cm-col-actions">
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Edit body camera"
                    aria-label="Edit body camera"
                    onClick={() => handleEdit(d)}
                  >
                    <i className="fas fa-pen"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <CameraDrawer
        open={!!editingDevice}
        device={editingDevice}
        onClose={() => setEditingDevice(null)}
        onSaved={refreshList}
      />
    </>
  );
}

